import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle, FiCheckCircle, FiRefreshCw, FiXCircle } from 'react-icons/fi';

import VikarCrmV2Panel from '../../components/admin/VikarCrmV2Panel';
import DanlonPayrollSettings from '../../components/admin/DanlonPayrollSettings';
import FinancialAccessEditor, { FinancialAccessFields, parseFinancialAccess } from '../../components/admin/FinancialAccessEditor';
import EvaTestAccessEditor from '../../components/admin/EvaTestAccessEditor';
import EvaTestBusinessAccessEditor from '../../components/admin/EvaTestBusinessAccessEditor';
import CredentialHandover from '../../components/admin/CredentialHandover';
import { useAuth } from '../../hooks/useAuth';
import integrationService from '../../services/api/integrationService';
import {
  API_SCOPES,
  isEvaScope,
  hasMixedEvaScopes,
  needsExplicitAccess,
  requiresSuperadminScopes,
  VIKARCRM_V2_INTEGRATION_PROVIDER,
  VIKARCRM_V2_REQUIRED_SCOPES,
  VIKARCRM_V2_REQUIRED_WEBHOOK_EVENTS,
  WEBHOOK_EVENT_TYPES,
} from './integrationCatalog';

const Page = styled.div`width: 100%; max-width: 1180px; min-width: 0;`;
const Title = styled.h1`font-size: 1.5rem; color: var(--ve-ink-800); margin: 0 0 0.25rem;`;
const Subtitle = styled.p`color: var(--ve-ink-600); margin: 0 0 1.5rem; font-size: 0.9rem;`;
const Card = styled.div`
  background: #fff; border-radius: 0.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  padding: 1.5rem; margin-bottom: 1.5rem; scroll-margin-top: 1rem;
`;
const CardHead = styled.div`display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem;`;
const Name = styled.h2`font-size: 1.15rem; color: var(--ve-ink-800); margin: 0;`;
const Desc = styled.p`color: var(--ve-ink-500); font-size: 0.85rem; margin: 0.25rem 0 0;`;
const Toggle = styled.label`
  position: relative; display: inline-block; width: 46px; height: 26px; flex-shrink: 0;
  input { opacity: 0; width: 0; height: 0; }
  span {
    position: absolute; inset: 0; cursor: pointer; background: ${({ $on }) => ($on ? 'var(--act-approve)' : '#c9d2d9')};
    border-radius: 999px; transition: 0.2s;
  }
  span:before {
    content: ''; position: absolute; height: 20px; width: 20px; left: 3px; bottom: 3px; background: #fff;
    border-radius: 50%; transition: 0.2s; transform: translateX(${({ $on }) => ($on ? '20px' : '0')});
  }
`;
const Fields = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-top: 1.25rem;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const Field = styled.div`display: flex; flex-direction: column;`;
const Label = styled.label`font-size: 0.8rem; font-weight: 600; color: var(--ve-ink-600); margin-bottom: 0.3rem; display: flex; gap: 0.4rem; align-items: center;`;
const Tag = styled.span`
  font-size: 0.65rem; font-weight: 600; padding: 0.05rem 0.4rem; border-radius: 999px;
  color: ${({ $set }) => ($set ? '#1e8449' : 'var(--ve-ink-500)')}; background: ${({ $set }) => ($set ? '#eafaf1' : 'var(--surface-subtle)')};
`;
const Input = styled.input`
  padding: 0.5rem 0.7rem; border: 1px solid var(--border-subtle); border-radius: 0.25rem; font-size: 0.9rem;
  &:focus { outline: none; border-color: var(--act-primary); }
`;
const SecretHint = styled.small`color: #b0b8bf; font-size: 0.7rem; margin-top: 0.2rem;`;
const Actions = styled.div`display: flex; gap: 0.75rem; align-items: center; margin-top: 1.25rem; flex-wrap: wrap;`;
const Btn = styled.button`
  padding: 0.55rem 1.1rem; border-radius: 0.25rem; font-size: 0.9rem; font-weight: 600; cursor: pointer; border: none;
  color: #fff; background: ${({ $variant }) => ($variant === 'ghost' ? 'var(--ve-ink-600)' : 'var(--act-primary)')};
  &:hover { filter: brightness(0.94); } &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const Result = styled.div`
  display: flex; align-items: center; gap: 0.4rem; font-size: 0.85rem;
  color: ${({ $ok, $warn }) => ($warn ? '#b45309' : $ok ? '#1e8449' : 'var(--act-danger)')};
`;
const Empty = styled.div`padding: 2rem; text-align: center; color: var(--ve-ink-500);`;

/* --- Integration-layer sections (API clients / webhooks) --- */
const TableWrap = styled.div`
  overflow-x: auto; margin-top: 1rem;
  @media (max-width: 640px) { display: none; }
`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Th = styled.th`
  text-align: left; padding: 0.5rem 0.6rem; font-size: 0.75rem; font-weight: 600; color: var(--ve-ink-600);
  border-bottom: 1px solid var(--border-subtle); white-space: nowrap;
`;
const Td = styled.td`
  padding: 0.55rem 0.6rem; font-size: 0.85rem; color: var(--ve-ink-800); border-bottom: 1px solid var(--surface-subtle);
  vertical-align: top;
`;
const ErrTd = styled(Td)`color: var(--act-danger); font-size: 0.75rem; max-width: 220px; word-break: break-word;`;
const CardList = styled.div`
  display: none;
  @media (max-width: 640px) { display: flex; flex-direction: column; gap: 0.75rem; margin-top: 1rem; }
`;
const ItemCard = styled.div`border: 1px solid var(--border-subtle); border-radius: 0.4rem; padding: 0.75rem;`;
const ItemHead = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start; gap: 0.5rem;
  font-weight: 600; font-size: 0.9rem; color: var(--ve-ink-800); word-break: break-all;
`;
const ItemMeta = styled.div`color: var(--ve-ink-500); font-size: 0.8rem; margin-top: 0.35rem; word-break: break-all;`;
const ItemError = styled(ItemMeta)`color: var(--act-danger);`;
const Pill = styled.span`
  display: inline-block; font-size: 0.7rem; font-weight: 600; padding: 0.1rem 0.5rem; border-radius: 999px; white-space: nowrap;
  color: ${({ $tone }) => ($tone === 'ok' ? '#1e8449' : $tone === 'warn' ? '#9a7d0a' : $tone === 'bad' ? 'var(--act-danger)' : 'var(--ve-ink-600)')};
  background: ${({ $tone }) => ($tone === 'ok' ? '#eafaf1' : $tone === 'warn' ? '#fef9e7' : $tone === 'bad' ? '#fdedec' : 'var(--surface-subtle)')};
`;
const Chip = styled.code`
  display: inline-block; font-size: 0.72rem; background: var(--surface-subtle); color: var(--ve-ink-800); border-radius: 0.25rem;
  padding: 0.05rem 0.35rem; margin: 0 0.25rem 0.25rem 0; overflow-wrap: break-word;
`;
const CheckGroup = styled.div`display: flex; flex-direction: column; gap: 0.45rem; margin-top: 0.3rem;`;
const CheckRow = styled.label`
  display: flex; align-items: baseline; gap: 0.5rem; font-size: 0.85rem; color: var(--ve-ink-800); cursor: pointer;
  input { flex-shrink: 0; }
`;
const FormBox = styled.div`border: 1px solid var(--border-subtle); border-radius: 0.4rem; padding: 1rem; margin-top: 1rem;`;
const SecretBox = styled.div`border: 1px solid var(--status-warning); background: #fef9e7; border-radius: 0.4rem; padding: 1rem; margin-top: 1rem;`;
const SecretTitle = styled.h3`font-size: 0.95rem; color: var(--ve-ink-800); margin: 0 0 0.4rem;`;
const SecretWarning = styled.p`
  display: flex; gap: 0.4rem; align-items: flex-start; color: #9a7d0a; font-size: 0.85rem; margin: 0 0 0.6rem;
  svg { flex-shrink: 0; margin-top: 0.15rem; }
`;
const SecretValue = styled.code`
  display: block; font-size: 0.85rem; background: #fff; border: 1px dashed #d4ac0d; border-radius: 0.25rem;
  padding: 0.5rem 0.7rem; word-break: break-all; user-select: all;
`;
const DangerBtn = styled.button`
  background: none; border: none; color: var(--act-danger); font-size: 0.85rem; font-weight: 600; cursor: pointer; padding: 0;
  &:hover { text-decoration: underline; } &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const SubHead = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin-top: 1.75rem;`;
const SubTitle = styled.h3`font-size: 0.95rem; color: var(--ve-ink-800); margin: 0;`;
const GhostSmallBtn = styled.button`
  display: inline-flex; align-items: center; gap: 0.35rem; background: none; border: 1px solid var(--border-subtle);
  border-radius: 0.25rem; color: var(--ve-ink-600); font-size: 0.8rem; font-weight: 600; padding: 0.3rem 0.7rem; cursor: pointer;
  &:hover { border-color: var(--act-primary); color: var(--act-primary); } &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const fmtDate = (value) => (value ? new Date(value).toLocaleDateString() : '');
const fmtDateTime = (value) => (value ? new Date(value).toLocaleString() : '');

const copyText = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) { /* fall through to legacy copy */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch (err) {
    return false;
  }
};

// One-time reveal box for a freshly created API key / webhook secret.
const SecretReveal = ({ title, warning, value, onClose }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyText(value);
    setCopied(ok);
  };

  return (
    <SecretBox>
      <SecretTitle>{title}</SecretTitle>
      <SecretWarning><FiAlertTriangle /> {warning}</SecretWarning>
      <SecretValue>{value}</SecretValue>
      <Actions>
        <Btn onClick={handleCopy}>
          {copied ? t('admin.integrations.copied') : t('admin.integrations.copy')}
        </Btn>
        <Btn $variant="ghost" onClick={onClose}>{t('common.close')}</Btn>
      </Actions>
    </SecretBox>
  );
};

const ScopeChips = ({ values }) => (values || []).map((v) => <Chip key={v}>{v}</Chip>);

const ApiClientsSection = ({ onChanged, canManageV2 }) => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [consumerId, setConsumerId] = useState('');
  const [scopes, setScopes] = useState([]);
  const [financialSources, setFinancialSources] = useState('');
  const [financialEmployers, setFinancialEmployers] = useState('');
  const [formError, setFormError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // { name, key } — shown once
  const [revokingId, setRevokingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await integrationService.listApiClients();
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleScope = (scope) => {
    setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
  };

  const handleCreate = async () => {
    if (!name.trim()) { setFormError(t('admin.integrations.apiClients.nameRequired')); return; }
    if (!/^[a-z0-9][a-z0-9._:-]{2,99}$/.test(consumerId)) { setFormError('Enter a stable consumer ID (3-100 lowercase characters).'); return; }
    if (scopes.length === 0) { setFormError(t('admin.integrations.apiClients.scopesRequired')); return; }
    if (hasMixedEvaScopes(scopes)) {
      setFormError(t('admin.integrations.apiClients.evaScopeConflict'));
      return;
    }
    if (requiresSuperadminScopes(scopes) && !canManageV2) {
      setFormError(t('admin.integrations.apiClients.restrictedSuperadminRequired'));
      return;
    }
    try {
      setCreating(true);
      setFormError(null);
      const data = await integrationService.createApiClient({ name: name.trim(), consumerId, scopes,
        ...(needsExplicitAccess(scopes)
          ? { financialAccess: parseFinancialAccess(financialSources, financialEmployers) } : {}) });
      setCreatedKey({ name: data.name, key: data.apiKey });
      setShowForm(false);
      setName('');
      setConsumerId('');
      setScopes([]);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (client) => {
    if (!window.confirm(t('admin.integrations.apiClients.confirmRevoke', { name: client.name }))) return;
    try {
      setRevokingId(client.id);
      setError(null);
      await integrationService.revokeApiClient(client.id);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setRevokingId(null);
    }
  };

  const statusPill = (client) => (
    <Pill $tone={client.revokedAt || (client.expiresAt && new Date(client.expiresAt) <= new Date()) ? 'bad' : 'ok'}>
      {client.revokedAt ? t('admin.integrations.apiClients.revoked') : client.expiresAt && new Date(client.expiresAt) <= new Date() ? 'Expired' : t('admin.integrations.apiClients.active')}
    </Pill>
  );

  return (
    <Card id="api-clients">
      <CardHead>
        <div>
          <Name>{t('admin.integrations.apiClients.title')}</Name>
          <Desc>{t('admin.integrations.apiClients.subtitle')}</Desc>
        </div>
        {!showForm && (
          <Btn onClick={() => { setShowForm(true); setFormError(null); }}>
            {t('admin.integrations.apiClients.create')}
          </Btn>
        )}
      </CardHead>

      {error && <Result $ok={false} style={{ marginTop: '0.75rem' }}><FiXCircle /> {error}</Result>}

      {showForm && (
        <FormBox>
          <Field>
            <Label htmlFor="api-client-name">{t('admin.integrations.apiClients.name')}</Label>
            <Input
              id="api-client-name"
              type="text"
              value={name}
              placeholder={t('admin.integrations.apiClients.namePlaceholder')}
              autoComplete="off"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field style={{ marginTop: '0.9rem' }}>
            <Label htmlFor="api-client-consumer">Consumer ID</Label>
            <Input id="api-client-consumer" value={consumerId} autoComplete="off"
              placeholder="vikarcrm-test" onChange={event => setConsumerId(event.target.value)} />
            <Desc>One active key per consumer. New keys expire after 90 days. Use reviewed replacement to rotate a consumer's key.</Desc>
          </Field>
          <Field style={{ marginTop: '0.9rem' }}>
            <Label as="span">{t('admin.integrations.apiClients.scopes')}</Label>
            {canManageV2 && (
              <GhostSmallBtn
                type="button"
                style={{ alignSelf: 'flex-start', marginBottom: '0.55rem' }}
                onClick={() => setScopes([...VIKARCRM_V2_REQUIRED_SCOPES])}
              >
                {t('admin.integrations.apiClients.vikarcrmV2Preset')}
              </GhostSmallBtn>
            )}
            <CheckGroup>
              {API_SCOPES.map((scope) => (
                <CheckRow key={scope.value}>
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope.value)}
                    disabled={creating || (!canManageV2 && requiresSuperadminScopes([scope.value]))
                      || (scopes.length > 0 && isEvaScope(scope.value) !== scopes.some(isEvaScope))}
                    onChange={() => toggleScope(scope.value)}
                  />
                  {t(scope.labelKey)} <Chip>{scope.value}</Chip>
                </CheckRow>
              ))}
            </CheckGroup>
          </Field>
          {needsExplicitAccess(scopes) && <FinancialAccessFields prefix="new-client"
            sources={financialSources} employers={financialEmployers} setSources={setFinancialSources}
            setEmployers={setFinancialEmployers} disabled={creating} />}
          <Actions>
            <Btn onClick={handleCreate} disabled={creating}>
              {creating ? t('admin.integrations.apiClients.creating') : t('admin.integrations.apiClients.create')}
            </Btn>
            <Btn $variant="ghost" onClick={() => { setShowForm(false); setFormError(null); }}>
              {t('common.cancel')}
            </Btn>
            {formError && <Result $ok={false}><FiXCircle /> {formError}</Result>}
          </Actions>
        </FormBox>
      )}

      {createdKey && (
        <SecretReveal
          title={t('admin.integrations.apiClients.keyCreated', { name: createdKey.name })}
          warning={t('admin.integrations.apiClients.keyWarning')}
          value={createdKey.key}
          onClose={() => setCreatedKey(null)}
        />
      )}

      {loading ? (
        <Empty>{t('common.loading')}</Empty>
      ) : clients.length === 0 ? (
        <Empty>{t('admin.integrations.apiClients.empty')}</Empty>
      ) : (
        <>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>{t('admin.integrations.apiClients.name')}</Th>
                  <Th>{t('admin.integrations.apiClients.scopes')}</Th>
                  <Th>{t('admin.integrations.apiClients.created')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id}>
                    <Td>{client.name}<ItemMeta>{client.consumerId}</ItemMeta><ItemMeta>{client.expiresAt ? `Expires ${fmtDate(client.expiresAt)}` : 'Legacy key: expiry requires reviewed rotation'}</ItemMeta></Td>
                    <Td><ScopeChips values={client.scopes} /></Td>
                    <Td>{fmtDate(client.createdAt)}</Td>
                    <Td>{statusPill(client)}</Td>
                    <Td>
                      {!client.revokedAt && canManageV2 && <FinancialAccessEditor client={client} onSaved={load} />}
                      {!client.revokedAt && canManageV2 && <><EvaTestAccessEditor client={client} onSaved={load} /><EvaTestBusinessAccessEditor client={client} onSaved={load} /></>}
                      {!client.revokedAt && canManageV2 && <CredentialHandover client={client} onCreated={async result => {
                        setCreatedKey({ name: result.name, key: result.apiKey }); await load();
                      }} />}
                      {!client.revokedAt && (canManageV2 || !requiresSuperadminScopes(client.scopes)) && (
                        <DangerBtn onClick={() => handleRevoke(client)} disabled={revokingId === client.id}>
                          {t('admin.integrations.apiClients.revoke')}
                        </DangerBtn>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
          <CardList>
            {clients.map((client) => (
              <ItemCard key={client.id}>
                <ItemHead>{client.name} {statusPill(client)}</ItemHead>
                <ItemMeta>{client.consumerId} ? {client.expiresAt ? `Expires ${fmtDate(client.expiresAt)}` : 'Legacy key: expiry requires reviewed rotation'}</ItemMeta>
                <ItemMeta><ScopeChips values={client.scopes} /></ItemMeta>
                <ItemMeta>{t('admin.integrations.apiClients.created')}: {fmtDate(client.createdAt)}</ItemMeta>
                {!client.revokedAt && canManageV2 && <FinancialAccessEditor client={client} onSaved={load} />}
                {!client.revokedAt && canManageV2 && <><EvaTestAccessEditor client={client} onSaved={load} /><EvaTestBusinessAccessEditor client={client} onSaved={load} /></>}
                {!client.revokedAt && canManageV2 && <CredentialHandover client={client} onCreated={async result => {
                  setCreatedKey({ name: result.name, key: result.apiKey }); await load();
                }} />}
                {!client.revokedAt && (canManageV2 || !requiresSuperadminScopes(client.scopes)) && (
                  <ItemMeta>
                    <DangerBtn onClick={() => handleRevoke(client)} disabled={revokingId === client.id}>
                      {t('admin.integrations.apiClients.revoke')}
                    </DangerBtn>
                  </ItemMeta>
                )}
              </ItemCard>
            ))}
          </CardList>
        </>
      )}
    </Card>
  );
};

const WebhooksSection = ({ onChanged, canManageV2 }) => {
  const { t } = useTranslation();
  const [subs, setSubs] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [whName, setWhName] = useState('');
  const [url, setUrl] = useState('');
  const [eventTypes, setEventTypes] = useState([]);
  const [integrationProvider, setIntegrationProvider] = useState(null);
  const [formError, setFormError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [createdSecret, setCreatedSecret] = useState(null); // { url, secret } — shown once
  const [deletingId, setDeletingId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadSubs = useCallback(async () => {
    const data = await integrationService.listWebhookSubscriptions();
    setSubs(Array.isArray(data) ? data : []);
  }, []);

  const loadDeliveries = useCallback(async () => {
    const data = await integrationService.listWebhookDeliveries(50);
    setDeliveries(Array.isArray(data) ? data : []);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([loadSubs(), loadDeliveries()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loadDeliveries, loadSubs]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleRefreshDeliveries = async () => {
    try {
      setRefreshing(true);
      setError(null);
      await loadDeliveries();
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const toggleEventType = (value) => {
    setEventTypes((prev) => (prev.includes(value) ? prev.filter((e) => e !== value) : [...prev, value]));
  };

  const handleAdd = async () => {
    if (!whName.trim()) { setFormError(t('admin.integrations.webhooks.nameRequired')); return; }
    if (!/^https?:\/\//i.test(url.trim())) { setFormError(t('admin.integrations.webhooks.urlRequired')); return; }
    if (eventTypes.length === 0) { setFormError(t('admin.integrations.webhooks.eventsRequired')); return; }
    try {
      setAdding(true);
      setFormError(null);
      const data = await integrationService.createWebhookSubscription({
        name: whName.trim(),
        url: url.trim(),
        eventTypes,
        integrationProvider,
      });
      setCreatedSecret({ url: data.name || data.url, secret: data.secret });
      setShowForm(false);
      setWhName('');
      setUrl('');
      setEventTypes([]);
      setIntegrationProvider(null);
      await loadSubs();
      if (onChanged) onChanged();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(t('admin.integrations.webhooks.confirmDelete', { url: sub.url }))) return;
    try {
      setDeletingId(sub.id);
      setError(null);
      await integrationService.deleteWebhookSubscription(sub.id);
      await loadSubs();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const deliveryStatus = (delivery) => {
    if (delivery.deliveredAt) return { tone: 'ok', label: t('admin.integrations.webhooks.statusDelivered') };
    if (delivery.deadAt) return { tone: 'bad', label: t('admin.integrations.webhooks.statusDead') };
    return { tone: 'warn', label: t('admin.integrations.webhooks.statusPending', { attempts: delivery.attempts }) };
  };

  const activePill = (sub) => (
    <Pill $tone={sub.active ? 'ok' : 'muted'}>
      {sub.active ? t('admin.integrations.webhooks.active') : t('admin.integrations.webhooks.inactive')}
    </Pill>
  );

  return (
    <Card id="webhooks">
      <CardHead>
        <div>
          <Name>{t('admin.integrations.webhooks.title')}</Name>
          <Desc>{t('admin.integrations.webhooks.subtitle')}</Desc>
        </div>
        {!showForm && (
          <Btn onClick={() => { setShowForm(true); setFormError(null); }}>
            {t('admin.integrations.webhooks.add')}
          </Btn>
        )}
      </CardHead>

      {error && <Result $ok={false} style={{ marginTop: '0.75rem' }}><FiXCircle /> {error}</Result>}

      {showForm && (
        <FormBox>
          <Field>
            <Label htmlFor="webhook-name">{t('admin.integrations.webhooks.name')}</Label>
            <Input
              id="webhook-name"
              type="text"
              value={whName}
              placeholder={t('admin.integrations.webhooks.namePlaceholder')}
              autoComplete="off"
              onChange={(e) => setWhName(e.target.value)}
            />
          </Field>
          <Field style={{ marginTop: '0.9rem' }}>
            <Label htmlFor="webhook-url">{t('admin.integrations.webhooks.url')}</Label>
            <Input
              id="webhook-url"
              type="url"
              value={url}
              placeholder={t('admin.integrations.webhooks.urlPlaceholder')}
              autoComplete="off"
              onChange={(e) => setUrl(e.target.value)}
            />
          </Field>
          {canManageV2 && (
            <Field style={{ marginTop: '0.9rem' }}>
              <Label as="span">{t('admin.integrations.webhooks.integration')}</Label>
              <CheckRow>
                <input
                  type="checkbox"
                  checked={integrationProvider === VIKARCRM_V2_INTEGRATION_PROVIDER}
                  onChange={(event) => {
                    if (event.target.checked) {
                      setIntegrationProvider(VIKARCRM_V2_INTEGRATION_PROVIDER);
                      setEventTypes((previous) => [
                        ...previous,
                        ...VIKARCRM_V2_REQUIRED_WEBHOOK_EVENTS.filter((value) => !previous.includes(value)),
                      ]);
                    } else {
                      setIntegrationProvider(null);
                    }
                  }}
                />
                {t('admin.integrations.webhooks.vikarcrmV2Preset')}
              </CheckRow>
            </Field>
          )}
          <Field style={{ marginTop: '0.9rem' }}>
            <Label as="span">{t('admin.integrations.webhooks.events')}</Label>
            <CheckGroup>
              {WEBHOOK_EVENT_TYPES.map((eventType) => (
                <CheckRow key={eventType.value}>
                  <input
                    type="checkbox"
                    checked={eventTypes.includes(eventType.value)}
                    onChange={() => toggleEventType(eventType.value)}
                  />
                  {t(eventType.labelKey)} <Chip>{eventType.value}</Chip>
                </CheckRow>
              ))}
            </CheckGroup>
          </Field>
          <Actions>
            <Btn onClick={handleAdd} disabled={adding}>
              {adding ? t('admin.integrations.webhooks.adding') : t('admin.integrations.webhooks.add')}
            </Btn>
            <Btn $variant="ghost" onClick={() => { setShowForm(false); setFormError(null); }}>
              {t('common.cancel')}
            </Btn>
            {formError && <Result $ok={false}><FiXCircle /> {formError}</Result>}
          </Actions>
        </FormBox>
      )}

      {createdSecret && (
        <SecretReveal
          title={t('admin.integrations.webhooks.secretCreated', { url: createdSecret.url })}
          warning={t('admin.integrations.webhooks.secretWarning')}
          value={createdSecret.secret}
          onClose={() => setCreatedSecret(null)}
        />
      )}

      {loading ? (
        <Empty>{t('common.loading')}</Empty>
      ) : (
        <>
          {subs.length === 0 ? (
            <Empty>{t('admin.integrations.webhooks.empty')}</Empty>
          ) : (
            <>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('admin.integrations.webhooks.name')}</Th>
                      <Th>{t('admin.integrations.webhooks.url')}</Th>
                      <Th>{t('admin.integrations.webhooks.events')}</Th>
                      <Th>{t('admin.integrations.webhooks.created')}</Th>
                      <Th>{t('common.status')}</Th>
                      <Th />
                    </tr>
                  </thead>
                  <tbody>
                    {subs.map((sub) => (
                      <tr key={sub.id}>
                        <Td>
                          {sub.name || '—'}
                          {sub.integrationProvider === VIKARCRM_V2_INTEGRATION_PROVIDER && (
                            <div><Chip>{t('admin.integrations.webhooks.vikarcrmV2Tag')}</Chip></div>
                          )}
                        </Td>
                        <Td style={{ wordBreak: 'break-all' }}>{sub.url}</Td>
                        <Td><ScopeChips values={sub.eventTypes} /></Td>
                        <Td>{fmtDate(sub.createdAt)}</Td>
                        <Td>{activePill(sub)}</Td>
                        <Td>
                          {(canManageV2 || sub.integrationProvider !== VIKARCRM_V2_INTEGRATION_PROVIDER) && (
                            <DangerBtn onClick={() => handleDelete(sub)} disabled={deletingId === sub.id}>
                              {t('common.delete')}
                            </DangerBtn>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
              <CardList>
                {subs.map((sub) => (
                  <ItemCard key={sub.id}>
                    <ItemHead>{sub.name || sub.url} {activePill(sub)}</ItemHead>
                    {sub.integrationProvider === VIKARCRM_V2_INTEGRATION_PROVIDER && (
                      <ItemMeta><Chip>{t('admin.integrations.webhooks.vikarcrmV2Tag')}</Chip></ItemMeta>
                    )}
                    <ItemMeta><ScopeChips values={sub.eventTypes} /></ItemMeta>
                    <ItemMeta>{t('admin.integrations.webhooks.created')}: {fmtDate(sub.createdAt)}</ItemMeta>
                    {(canManageV2 || sub.integrationProvider !== VIKARCRM_V2_INTEGRATION_PROVIDER) && (
                      <ItemMeta>
                        <DangerBtn onClick={() => handleDelete(sub)} disabled={deletingId === sub.id}>
                          {t('common.delete')}
                        </DangerBtn>
                      </ItemMeta>
                    )}
                  </ItemCard>
                ))}
              </CardList>
            </>
          )}

          <SubHead id="webhook-deliveries" style={{ scrollMarginTop: '1rem' }}>
            <SubTitle>{t('admin.integrations.webhooks.deliveries')}</SubTitle>
            <GhostSmallBtn onClick={handleRefreshDeliveries} disabled={refreshing}>
              <FiRefreshCw />
              {refreshing ? t('admin.integrations.webhooks.refreshing') : t('admin.integrations.webhooks.refresh')}
            </GhostSmallBtn>
          </SubHead>

          {deliveries.length === 0 ? (
            <Empty>{t('admin.integrations.webhooks.noDeliveries')}</Empty>
          ) : (
            <>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('admin.integrations.webhooks.event')}</Th>
                      <Th>{t('admin.integrations.webhooks.destination')}</Th>
                      <Th>{t('admin.integrations.webhooks.replayIds')}</Th>
                      <Th>{t('admin.integrations.webhooks.occurred')}</Th>
                      <Th>{t('common.status')}</Th>
                      <Th>{t('admin.integrations.webhooks.lastError')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.map((delivery) => {
                      const status = deliveryStatus(delivery);
                      return (
                        <tr key={delivery.id}>
                          <Td><Chip>{delivery.eventType}</Chip></Td>
                          <Td style={{ wordBreak: 'break-all' }}>{delivery.url}</Td>
                          <Td>{delivery.changeId || '—'} / {delivery.subscriptionId}</Td>
                          <Td>{fmtDateTime(delivery.occurredAt)}</Td>
                          <Td><Pill $tone={status.tone}>{status.label}</Pill></Td>
                          <ErrTd>{delivery.lastError || ''}</ErrTd>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
              <CardList>
                {deliveries.map((delivery) => {
                  const status = deliveryStatus(delivery);
                  return (
                    <ItemCard key={delivery.id}>
                      <ItemHead><Chip>{delivery.eventType}</Chip> <Pill $tone={status.tone}>{status.label}</Pill></ItemHead>
                      <ItemMeta>{delivery.url}</ItemMeta>
                      <ItemMeta>{t('admin.integrations.webhooks.replayIds')}: {delivery.changeId || '—'} / {delivery.subscriptionId}</ItemMeta>
                      <ItemMeta>{fmtDateTime(delivery.occurredAt)}</ItemMeta>
                      {delivery.lastError && (
                        <ItemError>{t('admin.integrations.webhooks.lastError')}: {delivery.lastError}</ItemError>
                      )}
                    </ItemCard>
                  );
                })}
              </CardList>
            </>
          )}
        </>
      )}
    </Card>
  );
};

const providerMeta = (provider, t) => {
  switch (provider) {
    case 'crmLifecycle':
      return { name: 'VikarCRM lifecycle coordination', desc: 'Use a separate lifecycle credential for reviewed customer and employee changes. Delivery requires the explicit lifecycle and integration rollout gates. Pending requests keep their original decisions and are reconciled before retry.' };
    case 'crm_payslips':
      return { name: 'VikarCRM payslip documents', desc: 'Send finalized Danløn payslips to the linked employee’s Documents in VikarCRM. Drafts are downloaded locally for admin review. Requires the companion CRM receiving API and a dedicated payslip key.' };
    case 'economic':
      return { name: t('admin.integrations.economicName'), desc: t('admin.integrations.economicDescription') };
    case 'danlon':
      return { name: t('admin.integrations.danlonName'), desc: t('admin.integrations.danlonDescription') };
    case 'smtp':
      return { name: t('admin.integrations.smtpName'), desc: t('admin.integrations.smtpDescription') };
    case 'crmEmployment':
      return {
        name: t('admin.integrations.crmEmploymentName', { defaultValue: 'VikarCRM employment corrections' }),
        desc: t('admin.integrations.crmEmploymentDescription', { defaultValue: 'Synchronize dated employment corrections with VikarCRM. Requires the CRM employment API and a dedicated key with employment:read and employment:write access.' }),
      };
    default:
      return { name: provider, desc: '' };
  }
};

const IntegrationCard = ({ integration, onSaved }) => {
  const { t, i18n } = useTranslation();
  const meta = providerMeta(integration.provider, t);
  const fields = integration.provider === 'danlon'
    ? integration.fields.filter((field) => !['companyCvr', 'payTypeCode', 'premieCode', 'forudbetalingCode', 'rejseomkCode', 'feriedageCode'].includes(field.key))
    : integration.fields;

  const [enabled, setEnabled] = useState(integration.enabled);
  const [values, setValues] = useState(() => {
    const v = {};
    integration.fields.forEach((f) => { v[f.key] = f.secret ? '' : (integration.config[f.key] || ''); });
    // Match the backend's compatibility default: saved two-callback clients
    // stay on legacy unless an administrator explicitly selects current OAuth.
    if (integration.provider === 'danlon' && !v.connectFlow) {
      v.connectFlow = integration.config.selectCompanyUrl && integration.config.code2TokenUrl ? 'legacy' : 'oauth';
    }
    return v;
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [result, setResult] = useState(null); // {ok, message}
  const [saved, setSaved] = useState(false);

  const buildConfigPayload = () => {
    const config = {};
    fields.forEach((f) => {
      // Secrets: only send when the user typed something (blank keeps stored).
      if (f.secret) {
        if (values[f.key] && values[f.key].trim() !== '') config[f.key] = values[f.key].trim();
      } else {
        config[f.key] = values[f.key];
      }
    });
    return config;
  };

  const handleSave = async () => {
    try {
      setSaving(true); setResult(null); setSaved(false);
      const updated = await integrationService.updateIntegration(integration.provider, {
        enabled,
        config: buildConfigPayload(),
      });
      setSaved(true);
      // Clear typed secrets (now stored) and reflect masked state.
      setValues((prev) => {
        const next = { ...prev };
        integration.fields.forEach((f) => { if (f.secret) next[f.key] = ''; });
        return next;
      });
      if (onSaved) onSaved(updated);
    } catch (err) {
      setResult({ ok: false, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true); setResult(null);
      const res = await integrationService.testIntegration(integration.provider, buildConfigPayload());
      // A green connection test does NOT mean mail will be sent: the app only
      // uses an ENABLED integration. Warn when the connection is fine but this
      // integration is currently switched off (or not yet saved as enabled), so
      // "test passes" can't be mistaken for "it works".
      if (res && res.ok && !enabled) {
        setResult({ ok: true, warn: true, message: `${res.message} — ${t('admin.integrations.testDisabledWarning')}` });
      } else {
        setResult(res);
      }
    } catch (err) {
      setResult({ ok: false, message: err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true); setResult(null);
      const url = await integrationService.getDanlonConnectUrl();
      // Full-page navigation into Danløn's OAuth flow; it redirects back to the
      // integrations page (?danlon=connected|error) when done.
      window.location.assign(url);
    } catch (err) {
      setResult({ ok: false, message: err.message });
      setConnecting(false);
    }
  };

  return (
    <Card>
      <CardHead>
        <div>
          <Name>{meta.name}</Name>
          <Desc>{meta.desc}</Desc>
        </div>
        <Toggle $on={enabled}>
          <input type="checkbox" aria-label={meta.name} checked={enabled} onChange={(e) => { setEnabled(e.target.checked); setSaved(false); }} />
          <span />
        </Toggle>
      </CardHead>

      <Fields>
        {fields.map((f) => (
          <Field key={f.key}>
            <Label htmlFor={`${integration.provider}-${f.key}`}>
              {t(`admin.integrations.fields.${f.key}`, { defaultValue: f.key === 'connectFlow' ? 'Danløn connection flow' : f.key === 'baseUrl' ? 'VikarCRM base URL' : f.key === 'apiKey' ? 'API key' : f.key === 'sourceInstance' ? 'Tempus instance ID (must match CRM)' : f.key })}
              {f.secret && (
                <Tag $set={integration.secretsSet[f.key]}>
                  {integration.secretsSet[f.key] ? t('admin.integrations.configured') : t('admin.integrations.notConfigured')}
                </Tag>
              )}
            </Label>
            {integration.provider === 'economic' && f.key === 'crmBillingSchedulePolicy' ? <>
              <Input as="select" id="economic-crmBillingSchedulePolicy" value={values.crmBillingSchedulePolicy || 'require_received'} onChange={(event) => {
                setValues((previous) => ({ ...previous, crmBillingSchedulePolicy: event.target.value })); setSaved(false);
              }}>
                <option value="require_received">Require received dated billing schedules</option>
                <option value="reviewed_crm_only">Approve CRM-only pricing when no schedule has ever arrived</option>
              </Input>
              <SecretHint>CRM-only compatibility is an explicit rollout decision. Any received or rejected schedule still controls readiness and cannot be bypassed.</SecretHint>
            </> : integration.provider === 'economic' && f.key === 'billingMode' ? <>
              <Input as="select" id="economic-billingMode" value={values.billingMode || 'crm'} onChange={(event) => {
                setValues((previous) => ({ ...previous, billingMode: event.target.value })); setSaved(false);
              }}>
                <option value="crm">CRM pricing with dated work and review</option>
                <option value="legacy">Legacy e-conomic product pricing</option>
              </Input>
              <SecretHint>CRM pricing requires a configured CRM connection. Choose legacy pricing only for an explicitly approved legacy billing workflow.</SecretHint>
            </> : integration.provider === 'danlon' && f.key === 'connectFlow' ? <>
              <Input as="select" id="danlon-connectFlow" value={values.connectFlow} onChange={(event) => {
                setValues((previous) => ({ ...previous, connectFlow: event.target.value })); setSaved(false);
              }} aria-describedby="danlon-connectFlow-help">
                {!['oauth', 'legacy'].includes(values.connectFlow) && <option value={values.connectFlow}>Unsupported saved flow — choose a supported option</option>}
                <option value="oauth">Current OAuth connection</option>
                <option value="legacy">Legacy connection with company selection</option>
              </Input>
              <SecretHint id="danlon-connectFlow-help">{values.connectFlow === 'legacy'
                ? 'Use the older two-callback client supplied by Danløn. Login is followed by the configured Select-company URL and Code-to-token URL; both are required. Existing settings with both URLs keep this flow.'
                : 'Use the current OAuth client supplied by Danløn. Company selection happens within authorization, and the callback exchanges the code directly for tokens. The separate Select-company and Code-to-token URLs are not used.'}</SecretHint>
              <SecretHint>Save the selected flow before Connect to Danløn. Choose the flow registered for your API client; this setting does not change the separate manual tax-card login.</SecretHint>
            </> : <Input
              id={`${integration.provider}-${f.key}`}
              type={f.secret ? 'password' : 'text'}
              value={values[f.key]}
              placeholder={f.secret && integration.secretsSet[f.key] ? '••••••••' : ''}
              autoComplete="off"
              onChange={(e) => { setValues((prev) => ({ ...prev, [f.key]: e.target.value })); setSaved(false); }}
            />}
            {(() => {
              // Optional per-field guidance (e.g. where each e-conomic token
              // comes from). Rendered only when a translation exists — checked
              // via i18n.exists() because returnEmptyString:false makes t()
              // echo the raw key for missing translations.
              const helpKey = `admin.integrations.fieldHelp.${integration.provider}.${f.key}`;
              return i18n.exists(helpKey) ? <SecretHint>{t(helpKey)}</SecretHint> : null;
            })()}
            {f.secret && <SecretHint>{t('admin.integrations.secretHint')}</SecretHint>}
          </Field>
        ))}
      </Fields>

      <Actions>
        <Btn onClick={handleSave} disabled={saving}>
          {saving ? t('admin.integrations.saving') : t('admin.integrations.save')}
        </Btn>
        <Btn $variant="ghost" onClick={handleTest} disabled={testing}>
          {testing ? t('admin.integrations.testing') : t('admin.integrations.test')}
        </Btn>
        {integration.provider === 'danlon' && (
          <Btn $variant="ghost" onClick={handleConnect} disabled={connecting}>
            {connecting ? t('admin.integrations.connect.connecting') : t('admin.integrations.connect.danlonButton')}
          </Btn>
        )}
        {saved && <Result $ok><FiCheckCircle /> {t('admin.integrations.saved')}</Result>}
        {result && (
          <Result $ok={result.ok} $warn={result.warn}>
            {result.warn ? <FiAlertTriangle /> : result.ok ? <FiCheckCircle /> : <FiXCircle />} {result.message}
          </Result>
        )}
      </Actions>
      {integration.provider === 'danlon' && (
        <SecretHint style={{ marginTop: '0.6rem' }}>{t('admin.integrations.connect.hint')}</SecretHint>
      )}
      {integration.provider === 'crmEmployment' && (
        <SecretHint style={{ marginTop: '0.6rem' }}>
          {t('admin.integrations.crmEmploymentHelp', { defaultValue: 'Enter the VikarCRM server base URL without an API path. This connection becomes usable only after the matching CRM employment API is deployed and the key has the required permissions. Saving credentials does not confirm a connection. Open an employee’s Employment correction to check access; pending or failed corrections stay visibly unconfirmed.' })}
        </SecretHint>
      )}
    </Card>
  );
};

const Integrations = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Result of the Danløn connect flow (read once from the ?danlon= redirect).
  const [connectNotice, setConnectNotice] = useState(null); // { ok, message }
  const [v2DependencyRevision, setV2DependencyRevision] = useState(0);
  const isSuperAdmin = currentUser?.isSuperAdmin === true;

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await integrationService.getIntegrations();
      setIntegrations(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Danløn's OAuth flow redirects back here with ?danlon=connected|error. Show
  // the outcome, then strip the params so a refresh doesn't repeat the banner.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('danlon');
    if (!status) return;
    if (status === 'connected') {
      setConnectNotice({ ok: true, message: t('admin.integrations.connect.connected') });
    } else {
      setConnectNotice({ ok: false, message: t('admin.integrations.connect.error', { message: params.get('message') || '' }) });
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, [t]);

  if (loading) return <Empty>{t('common.loading')}</Empty>;

  return (
    <Page>
      <Title>{t('admin.integrations.title')}</Title>
      <Subtitle>{t('admin.integrations.subtitle')}</Subtitle>
      {connectNotice && (
        <Card>
          <Result $ok={connectNotice.ok}>
            {connectNotice.ok ? <FiCheckCircle /> : <FiXCircle />} {connectNotice.message}
          </Result>
        </Card>
      )}
      {error && <Card><Result $ok={false}><FiXCircle /> {error}</Result></Card>}
      {isSuperAdmin && (
        <VikarCrmV2Panel refreshToken={v2DependencyRevision} />
      )}
      {/* The save response IS the fresh public view — secretsSet included — so
          it replaces this provider's row. It used to be discarded into a no-op
          callback, which is why a secret saved successfully still showed
          "Not configured" until the operator hard-refreshed: the card cleared
          its own input (the secret is stored now) while the badge kept
          rendering page-load state. That combination reads as "the save was
          ignored", which is the one thing it never was. */}
      <DanlonPayrollSettings
        integration={integrations.find((entry) => entry.provider === 'danlon')}
        onIntegrationSaved={(updated) => setIntegrations((prev) => prev.map((row) => row.provider === updated.provider ? updated : row))}
      />
      {integrations.filter((integration) => integration.provider !== 'crmPayroll').map((integration) => (
        <IntegrationCard
          key={integration.provider}
          integration={integration}
          onSaved={(updated) => setIntegrations((prev) => prev.map(
            (row) => (row.provider === updated.provider ? updated : row)
          ))}
        />
      ))}
      <ApiClientsSection
        canManageV2={isSuperAdmin}
        onChanged={() => setV2DependencyRevision((revision) => revision + 1)}
      />
      <WebhooksSection
        canManageV2={isSuperAdmin}
        onChanged={() => setV2DependencyRevision((revision) => revision + 1)}
      />
    </Page>
  );
};

export default Integrations;
